import * as FileSaver from "file-saver";
import { zipSync, strToU8 } from "fflate";
import { apiCall } from "./action";

// summaries exist only for home-team (PTC) companies and ratings only for ISN companies.
const COMMON_COMMENT_LIBRARY_RESOURCES = ["comments", "lists", "tags"];
const HT_ONLY_RESOURCE = "summaries";
const NON_HT_ONLY_RESOURCE = "ratings";

// When the company type is unknown, request both; the API returns an empty list for the one
// that does not apply and it is skipped from the zip.
const getCommentLibraryResources = (isHtCompany) => {
  if (isHtCompany === undefined || isHtCompany === null) {
    return [...COMMON_COMMENT_LIBRARY_RESOURCES, HT_ONLY_RESOURCE, NON_HT_ONLY_RESOURCE];
  }
  return [
    ...COMMON_COMMENT_LIBRARY_RESOURCES,
    isHtCompany ? HT_ONLY_RESOURCE : NON_HT_ONLY_RESOURCE,
  ];
};
const HT_ACCOUNT_TYPE = 1;
const HT_KEY_PREFIXES = ["ht-", "hb-"];

// Mirrors RW_ParentApp::isHtCompanyKey() in pt-nextgen: account_type 1 or an ht-/hb- key is home team.
export const isHtCompany = (companyKey, accountType) =>
  Number(accountType) === HT_ACCOUNT_TYPE ||
  HT_KEY_PREFIXES.some((prefix) => String(companyKey ?? "").startsWith(prefix));

const INVALID_FILE_NAME_CHARS = /[\\/:*?"<>|]/g;

const toSafeFileName = (fileName) => fileName.replace(INVALID_FILE_NAME_CHARS, "_");

// apiCall delivers failures to the callback, so success has to be checked on the body.
const fetchExportFile = (endPointKey, payload) =>
  new Promise((resolve, reject) => {
    apiCall(
      (response) => {
        const body = response?.data;
        if (body?.success) resolve(body);
        else reject(new Error(body?.message || "Download failed. Please try again."));
      },
      payload,
      endPointKey
    );
  });

const toJsonBytes = (data) => strToU8(JSON.stringify(data, null, 2));

const saveZip = (files, zipName) => {
  const blob = new Blob([zipSync(files)], { type: "application/zip" });
  FileSaver.saveAs(blob, toSafeFileName(zipName));
};

export const downloadTemplateJson = async (parentAppId, templateId) => {
  const { file_name, data } = await fetchExportFile("EXPORT_TEMPLATE", {
    parent_app_id: parentAppId,
    template_id: templateId,
  });
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  FileSaver.saveAs(blob, toSafeFileName(file_name));
};

export const downloadTemplatesZip = async (parentAppId, templateIds, companyKey) => {
  const files = {};
  // Sequential on purpose: each template is a heavy query on the backend.
  for (const templateId of templateIds) {
    const { file_name, data } = await fetchExportFile("EXPORT_TEMPLATE", {
      parent_app_id: parentAppId,
      template_id: templateId,
    });
    files[toSafeFileName(file_name)] = toJsonBytes(data);
  }
  saveZip(files, `Templates - ${companyKey ?? parentAppId}.zip`);
};

export const downloadCommentLibraryZip = async (parentAppId, companyKey, isHtCompany) => {
  const files = {};
  for (const resource of getCommentLibraryResources(isHtCompany)) {
    const { file_name, data } = await fetchExportFile("EXPORT_COMMENT_LIBRARY", {
      parent_app_id: parentAppId,
      resource,
    });
    const isNotApplicable =
      [HT_ONLY_RESOURCE, NON_HT_ONLY_RESOURCE].includes(resource) && !data?.length;
    if (!isNotApplicable) files[toSafeFileName(file_name)] = toJsonBytes(data);
  }
  saveZip(files, `Comment Library - ${companyKey ?? parentAppId}.zip`);
};
